/**
 * Hook for appointment CRUD mutations
 */

import { toast } from 'sonner'
import { trpc } from '@/trpc/client'
import { formatDateForApi, calculateDuration } from '@/lib/calendar-utils'
import type { AppointmentFormData } from '@/types/calendar'

/**
 * Create appointment mutation
 */
export function useCreateAppointment() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.create.useMutation({
		onSuccess: () => {
			toast.success('Termín byl úspěšně vytvořen')
			utils.calendar.appointment.list.invalidate()
			utils.calendar.appointment.listRepeating.invalidate()
			utils.calendar.appointment.dayStats.invalidate()
		},
		onError: (error) => {
			toast.error(`Chyba při vytváření termínu: ${error.message}`)
		},
	})
}

/**
 * Update appointment mutation
 */
export function useUpdateAppointment() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.update.useMutation({
		onSuccess: (data) => {
			if (data.cancelled) {
				toast.info('Termín byl označen jako zrušený')
			} else {
				toast.success('Termín byl úspěšně upraven')
			}
			utils.calendar.appointment.list.invalidate()
			utils.calendar.appointment.listRepeating.invalidate()
			utils.calendar.appointment.getById.invalidate()
			utils.calendar.appointment.dayStats.invalidate()
			utils.calendar.settings.cancelled.invalidate()
			utils.calendar.settings.noshow.invalidate()
		},
		onError: (error) => {
			toast.error(`Chyba při ukládání: ${error.message}`)
		},
	})
}

/**
 * Delete appointment mutation
 */
export function useDeleteAppointment() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.delete.useMutation({
		onSuccess: () => {
			toast.success('Termín byl smazán')
			utils.calendar.appointment.list.invalidate()
			utils.calendar.appointment.listRepeating.invalidate()
			utils.calendar.appointment.dayStats.invalidate()
		},
		onError: (error) => {
			toast.error(`Chyba při mazání: ${error.message}`)
		},
	})
}

/**
 * Confirm temporary appointments
 */
export function useConfirmAppointments() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.confirm.useMutation({
		onSuccess: (data) => {
			toast.success(`${data.confirmed} termínů bylo potvrzeno`)
			utils.calendar.appointment.list.invalidate()
		},
		onError: (error) => {
			toast.error(`Chyba při potvrzování: ${error.message}`)
		},
	})
}

/**
 * Reschedule appointment (drag & drop)
 * Optimistic updates are handled at the component level
 */
export function useRescheduleAppointment() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.reschedule.useMutation({
		onSuccess: () => {
			toast.success('Termín byl přesunut')
		},
		onError: (error) => {
			toast.error(`Chyba při přesunu: ${error.message}`)
		},
		onSettled: () => {
			// Refetch to ensure we have fresh data
			utils.calendar.appointment.list.invalidate()
			utils.calendar.appointment.listRepeating.invalidate()
			utils.calendar.appointment.dayStats.invalidate()
		},
	})
}

/**
 * Delete temporary appointments
 */
export function useDeleteTempAppointments() {
	const utils = trpc.useUtils()

	return trpc.calendar.appointment.deleteTemp.useMutation({
		onSuccess: (data) => {
			toast.success(`${data.deleted} dočasných termínů bylo smazáno`)
			utils.calendar.appointment.list.invalidate()
		},
		onError: (error) => {
			toast.error(`Chyba při mazání: ${error.message}`)
		},
	})
}

/**
 * Transform form data to API input for create
 */
export function transformFormDataForCreate(data: AppointmentFormData) {
	return {
		date: formatDateForApi(data.date),
		startTime: data.startTime,
		endTime: data.endTime,
		duration: calculateDuration(data.startTime, data.endTime),
		title: data.title,
		patientIds: data.patientIds,
		patients: data.patients ?? [],
		priceFrom: data.priceFrom ?? null,
		priceTo: data.priceTo ?? null,
		note: data.note ?? null,
		background: data.background ?? null,
		actionType: data.actionType ?? null,
		repeatAfter: data.repeatAfter,
		endDate: data.endDate ? formatDateForApi(data.endDate) : null,
		smsRequired: data.smsRequired,
		type: data.type,
		doctorsNote: data.doctorsNote ?? null,
	}
}

/**
 * Transform form data to API input for update
 */
export function transformFormDataForUpdate(
	id: string,
	data: AppointmentFormData,
) {
	return {
		id,
		date: formatDateForApi(data.date),
		startTime: data.startTime,
		endTime: data.endTime,
		duration: calculateDuration(data.startTime, data.endTime),
		title: data.title,
		patientIds: data.patientIds,
		patients: data.patients ?? [],
		priceFrom: data.priceFrom ?? null,
		priceTo: data.priceTo ?? null,
		note: data.note ?? null,
		doctorsNote: data.doctorsNote ?? null,
		background: data.background ?? null,
		actionType: data.actionType ?? null,
		repeatAfter: data.repeatAfter,
		endDate: data.endDate ? formatDateForApi(data.endDate) : null,
		smsRequired: data.smsRequired,
		type: data.type,
		noShowUpPaid: data.noShowUpPaid ?? false,
		checkedInPersonalIds: data.checkedInPersonalIds,
	}
}
